// =========================
// Dashboard & Reports Styles
// =========================

import { flexRow, flexCol, flexColumnCenter, flexRowBetween } from "./flex";
import { spaces, spacesPercent } from "./spacing";
import { colors } from "./colors";


// =========================
// Chart helpers
// =========================

export const chartPaper = {
  ...flexRow,
  alignItems: "stretch",
  minHeight: `calc(${spaces[6]} * 7.5)`,
  padding: spaces[1],
  flex: "1",
  display: "flex",
  width: "100%",
  height: "100%",
  overflow: "hidden",
  boxSizing: "border-box",
};

export const dashboardWidgetPaper = {
  ...chartPaper,
  height: 420,
  minHeight: 420,
  maxHeight: 420,
};

export const chartFill = {
  ...flexCol,
  flex: 1,
};

export const chartCenteredGrid = {
  ...flexColumnCenter,
  flex: 1,
};

export const chartFillCenter = {
  ...chartFill,
  height: "100%",
  display: "flex",
  alignItems: "center",
};

export const chartCenteredFull = {
  ...chartCenteredGrid,
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

export const chartSquareBox = {
  width: `min(calc(${spaces[6]} * 10), ${spacesPercent[100]})`,
  height: `min(calc(${spaces[6]} * 10), ${spacesPercent[100]})`,
  display: "grid",
  placeItems: "center",
};

export const chartOuter = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
  minWidth: 0,
  overflow: "hidden",
};

export const chartInner = {
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 0,
  maxWidth: "100%",
  overflow: "hidden",
};

// Compact summary labels under chart
export const summaryLabelsRow = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  justifyContent: "center",
  gap: spaces[1],
  marginTop: spaces[1],
};

export const summaryLabelItem = {
  display: "flex",
  alignItems: "center",
  gap: spaces[0.5],
  textAlign: "center",
};

export const summaryLabelIcon = {
  fontSize: 18,
  color: (theme: any) => theme.palette.mode === 'dark' ? colors.lightText : colors.black,
};

// =========================
// Statistical cards (reports)
// =========================

export const statCardPaper = {
  display: "flex",
  alignItems: "center",
  gap: spaces[1],
  p: spaces[1.5],
  borderRadius: 2,
  width: "100%",
  maxHeight: 220,
  overflowY: "auto",
  backgroundColor: (theme: any) => theme.palette.background.paper,
  boxShadow: (theme: any) => theme.palette.mode === 'dark' 
    ? `0 4px 12px ${colors.darkShadowOpacity40}` 
    : `0 2px 8px ${colors.darkShadowOpacity10}`,
};

export const statCardIconBox = {
  borderRadius: 1,
  p: spaces[1],
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: (theme: any) => theme.palette.mode === 'dark' ? colors.lightText : colors.white,
};

export const statCardContent = {
  ml: 1,
};

export const statCardGrow = {
  flex: "1 1 0",
  minHeight: 0,
};

export const statCardSubText = {
  marginTop: spaces[0.5],
};

// =========================
// Compact summary strip (reports)
// =========================

export const summaryStripRow = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "stretch",
  justifyContent: "flex-start",
  gap: spaces[1],
  paddingBottom: spaces[1],
  paddingRight: "56px",
};

export const summaryPill = {
  display: "flex",
  alignItems: "center",
  gap: spaces[1],
  padding: `${spaces[0.5]} ${spaces[1]}`,
  borderRadius: 2,
  flex: "1 1 auto",
  minWidth: 150,
  maxWidth: 280,
  backgroundColor: (theme: any) => theme.palette.background.paper,
  boxShadow: (theme: any) => theme.palette.mode === 'dark'
    ? `0 4px 12px ${colors.darkShadowOpacity40}`
    : `0 2px 8px ${colors.darkShadowOpacity10}`,
};

export const summaryPillIconBox = {
  borderRadius: 1,
  p: spaces[0.5],
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: (theme: any) => theme.palette.mode === 'dark' ? colors.lightText : colors.white,
};

export const summaryPillContent = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  minWidth: 0,
};

export const summaryPillValue = {
  fontSize: "15px",
  fontWeight: 600,
  lineHeight: 1.2,
  color: (theme: any) => theme.palette.mode === 'dark' ? colors.lightText : colors.black,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  maxWidth: "100%",
};

export const summaryPillLabel = {
  fontSize: "12px",
  lineHeight: 1.2,
  color: (theme: any) => theme.palette.mode === 'dark' ? colors.lightText : colors.textSecondary,
};

// =========================
// Interactive legend chips (reports)
// =========================

export const legendChipsRow = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  justifyContent: "center",
  gap: spaces[0.5],
  marginTop: spaces[1],
  overflowX: "hidden",
};

export const legendChip = {
  display: "flex",
  alignItems: "center",
  gap: spaces[0.5],
  padding: `${spaces[0.5]} ${spaces[1]}`,
  borderRadius: 20,
  cursor: "pointer",
  userSelect: "none",
  minWidth: 0,
  maxWidth: "100%",
  backgroundColor: (theme: any) => theme.palette.mode === 'dark' ? '#2a2a2a' : colors.background,
  border: `1px solid ${colors.border}`,
  transition: "all 0.2s ease",
  "&:hover": {
    borderColor: colors.primary,
  },
};

export const legendChipDot = {
  width: 12,
  height: 12,
  borderRadius: 3,
  flexShrink: 0,
};

export const legendChipText = {
  fontSize: "13px",
  fontWeight: 500,
  color: (theme: any) => theme.palette.mode === 'dark' ? colors.lightText : colors.textPrimary,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  maxWidth: "100%",
};

export const legendChipHidden = {
  opacity: 0.45,
  textDecoration: "line-through",
};

// =========================
// Report view helpers
// =========================

// Growable container for the reports page: fills the viewport but allows the
// page to scroll when content (e.g. many legend chips) exceeds the height.
export const reportsPaperContainer = {
  display: "flex",
  flexDirection: "column",
  gap: spaces[1],
  padding: spaces["1.5"],
  minHeight: "calc(100vh - 135px)",
  height: "auto",
};

// =========================
// Download button for charts
// =========================

export const downloadChartButton = {
  position: "absolute",
  top: 8,
  right: 8,
  zIndex: 10,
  minWidth: "auto",
  padding: "8px",
};

// =========================
// Export container divs (for PDF export)
// =========================

export const exportContainerDiv = {
  width: '100%',
  minHeight: '100%',
  height: 'auto',
  padding: spaces[1.5],
  backgroundColor: (theme: any) => theme.palette.background.default,
  boxSizing: 'border-box' as const,
  display: 'flex',
  flexDirection: 'column' as const,
};

export const exportContainerNone = {
  width: "100%",
  height: "100%",
  padding: "20px",
  backgroundColor: "white",
};

export const exportContainerLabelsBottom = {
  width: "100%",
  height: "100%",
  padding: "20px",
  backgroundColor: "white",
};

// =========================
// Widgets responsive grid
// =========================

export const widgetsGrid = {
  display: "grid",
  gap: spaces[1],
  justifyContent: "center",
  alignItems: "stretch",
  width: "100%",
  gridTemplateColumns: "repeat(auto-fit, minmax(480px, 1fr))",
};

// =========================
// Dashboard detail helpers
// =========================

export const dashboardDetailTop = {
  ...flexRowBetween,
  alignItems: "flex-start",
};

export const dashboardSummaryCol = {
  ...flexCol,
  gap: spaces[0.5],
};

export const dashboardActionsRow = {
  ...flexRow,
  alignItems: "center",
  gap: spaces[1],
};

export const dashboardHeaderRight = {
  ...flexRow,
  alignItems: "center",
  gap: spaces[1],
};

export const headerActionsSpaced = {
  ...flexRow,
  alignItems: "center",
  gap: spaces[0.5],
  marginLeft: spaces[2],
};
